'use client'

import { Settable } from '@/components/page-specific/catering/common/utils/typing'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import useTenant from '@/hooks/useTenant'
import { addDurationToDateConsideringSomeDays } from '@caterdesk/utils--gm-validation'

import * as dateFns from 'date-fns'
import { useMemo } from 'react'

import {
  computeDeliveryTimeSlots,
  type UserBasketSettings,
} from '../../common/domain/user-basket-settings'
import { useUserBasketSettingsState } from '../../common/states/useUserBasketSettingsState'

type State = {
  userBasketSettings: Settable<UserBasketSettings>
  minAllowedDeliveryDate: Date
  deliveryTimeSlots: string[]
}

export const useVendorListPageState = (): State => {
  const tenant = useTenant()
  const splitOn = useFeatureFlag(SPLITS.SAME_DAY_GM_DELIVERY)
  const cutOff5pm = useFeatureFlag(SPLITS.GM_CUTOFF_5PM)

  const minAllowedDeliveryDate = useMemo(() => {
    let minHours = 24
    if (splitOn) minHours = 2
    if (cutOff5pm) {
      const now = new Date()
      const currentHour = now.getHours()
      minHours = currentHour >= 17 ? 48 : 24
      if (dateFns.isWeekend(now)) {
        const dayOfWeek = dateFns.getDay(now)
        minHours += 24 * (dayOfWeek === 0 ? 2 : 1)
      }
      minHours -= currentHour
    }

    return addDurationToDateConsideringSomeDays(new Date(), { hours: minHours }, (date) => {
      if (dateFns.isWeekend(date)) return false

      const deliveryTimeSlots = computeDeliveryTimeSlots({
        deliveryDate: date,
        minAllowedDateTime: null,
        useDeliveryTimes: cutOff5pm,
      })

      return deliveryTimeSlots.length > 0
    })
  }, [splitOn, cutOff5pm])

  const userBasketSettingsState = useUserBasketSettingsState({
    resetDateTimeBefore: { date: minAllowedDeliveryDate },
  })

  const deliveryTimeSlots = computeDeliveryTimeSlots({
    deliveryDate: userBasketSettingsState.value.date
      ? new Date(userBasketSettingsState.value.date)
      : null,
    minAllowedDateTime: minAllowedDeliveryDate,
    useDeliveryTimes: cutOff5pm,
    deliveryTimeRange: {
      start: tenant?.features?.gmDeliveryTimeslots.start ?? '',
      end: tenant?.features?.gmDeliveryTimeslots.end ?? '',
    },
  })

  return {
    userBasketSettings: userBasketSettingsState,
    minAllowedDeliveryDate,
    deliveryTimeSlots,
  }
}
